import { useMutation } from '@apollo/client'
import { useCallback } from 'react'
import { graphql } from '~publish/gql'
import { useCurrentOrganization } from '~publish/contexts/AccountContext'
import type { SavedRepliesQueryQuery } from '~publish/gql/graphql'
import { SavedRepliesQuery } from './useSavedRepliesQuery'
import type { SavedReplyId } from '../../types'
import { handleSavedReplyError } from './handleSavedReplyError'

export type DeleteSavedReply = (params: {
  id: SavedReplyId
}) => Promise<SavedReplyId>

export const DeleteSavedReply = graphql(/* GraphQL */ `
  mutation DeleteSavedReply($input: DeleteSavedReplyInput!) {
    deleteSavedReply(input: $input) {
      ... on DeleteSavedReplySuccess {
        id
      }
      ... on MutationError {
        __typename
        message
      }
    }
  }
`)

/**
 * Hook to delete a saved reply.
 *
 * @returns {DeleteSavedReply} Function to delete a saved reply.
 * @example
 * const deleteSavedReply = useDeleteSavedReply();
 *
 * const handleDeleteSavedReply = async () => {
 *   try {
 *     const deletedSavedReplyId = await deleteSavedReply({ id: '123' });
 *   } catch (err) {
 *     console.error(err);
 *   }
 * };
 */
export function useDeleteSavedReply(): DeleteSavedReply {
  const organizationId = useCurrentOrganization()?.id

  const [deleteSavedReplyMutation] = useMutation(DeleteSavedReply, {
    update(cache, { data }) {
      if (
        !data?.deleteSavedReply ||
        data.deleteSavedReply.__typename !== 'DeleteSavedReplySuccess'
      )
        return

      const deletedId = data.deleteSavedReply.id

      const variables = {
        input: { organizationId },
      }

      // Read from the cache with correct variables
      const existingData = cache.readQuery<SavedRepliesQueryQuery>({
        query: SavedRepliesQuery,
        variables,
      })

      if (!existingData) return

      // Filter out the deleted item
      const newItems = existingData.savedReplies.savedReplies.filter(
        (item) => item.id !== deletedId,
      )

      cache.writeQuery<SavedRepliesQueryQuery>({
        query: SavedRepliesQuery,
        variables,
        data: {
          savedReplies: {
            __typename: 'SavedRepliesResponse',
            savedReplies: newItems,
          },
        },
      })
    },
  })

  const deleteSavedReply = useCallback(
    async ({ id }: { id: string }): Promise<SavedReplyId> => {
      if (!id) {
        throw new Error('Saved Reply Id is required')
      }

      const { data, errors } = await deleteSavedReplyMutation({
        variables: {
          input: {
            id,
          },
        },
      })

      if (errors || !data) {
        throw new Error(errors?.[0]?.message ?? 'Failed to delete saved reply')
      }

      const result = data.deleteSavedReply

      if (result.__typename !== 'DeleteSavedReplySuccess') {
        handleSavedReplyError({
          typename: result.__typename ?? '',
          operation: 'delete',
          element: 'saved reply',
        })
        /* needed only for TS to be happy */
        throw new Error('Unexpected error')
      }

      return result.id
    },
    [deleteSavedReplyMutation],
  )

  return deleteSavedReply
}
